<template>
  <div v-if="showIndicator" class="prefilled-field-indicator mt-5">
    <!-- Pre-filled indicator (blue) -->
    <div
      v-if="!isEdited && !isCleared"
      class="flex items-center justify-between gap-2 rounded-md bg-blue-50 px-3 py-2 text-sm border border-blue-200"
    >
      <div class="flex items-center gap-2">
        <svg
          class="h-4 w-4 text-blue-600 flex-shrink-0"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path
            stroke-linecap="round"
            stroke-linejoin="round"
            stroke-width="2"
            d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
          />
        </svg>
        <span class="text-blue-900">
          Pre-filled from {{ sourceLabel }}
          <span v-if="formattedDate" class="text-blue-700">({{ formattedDate }})</span>
        </span>
      </div>
      <button
        v-if="allowClear"
        @click.prevent="handleClear"
        type="button"
        class="text-blue-600 hover:text-blue-800 font-medium text-xs underline"
      >
        Clear Response
      </button>
    </div>

    <!-- Edited indicator (orange) -->
    <div
      v-if="isEdited && !isCleared"
      class="flex items-center gap-2 rounded-md bg-orange-50 px-3 py-2 text-sm border border-orange-200"
    >
      <svg
        class="h-4 w-4 text-orange-600 flex-shrink-0"
        fill="none"
        viewBox="0 0 24 24"
        stroke="currentColor"
      >
        <path
          stroke-linecap="round"
          stroke-linejoin="round"
          stroke-width="2"
          d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z"
        />
      </svg>
      <span class="text-orange-900">You've edited this field</span>
    </div>
  </div>
</template>

<script>
export default {
  name: "PrefilledFieldIndicator",
  props: {
    prefillMetadata: {
      type: Object,
      default: null,
      validator: (value) => {
        if (!value) return true
        return (
          typeof value.source === "string" &&
          typeof value.source_field === "string"
        )
      },
    },
    fieldValue: {
      type: [String, Number, Boolean, Array, Object],
      default: null,
    },
    originalValue: {
      type: [String, Number, Boolean, Array, Object],
      default: null,
    },
    allowClear: {
      type: Boolean,
      default: true,
    },
    isCleared: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      initialValue: null,
    }
  },
  computed: {
    showIndicator() {
      return this.prefillMetadata && this.prefillMetadata.source
    },
    sourceLabel() {
      if (!this.prefillMetadata) return ""

      // Convert source to human-readable label
      const sourceMap = {
        sign_up: "sign-up form",
        member_profile: "member profile",
        past_present: "Past & Present form",
      }

      return sourceMap[this.prefillMetadata.source] || this.prefillMetadata.source
    },
    formattedDate() {
      if (!this.prefillMetadata || !this.prefillMetadata.source_updated_at) return ""

      try {
        const date = new Date(this.prefillMetadata.source_updated_at)
        return date.toLocaleDateString("en-GB", {
          day: "numeric",
          month: "short",
          year: "numeric",
        })
      } catch (e) {
        return ""
      }
    },
    isEdited() {
      // Check if the current value differs from the original prefilled value
      if (this.initialValue === null) return false

      // Handle different value types
      if (Array.isArray(this.fieldValue) && Array.isArray(this.initialValue)) {
        return JSON.stringify(this.fieldValue) !== JSON.stringify(this.initialValue)
      }

      if (typeof this.fieldValue === "object" && typeof this.initialValue === "object") {
        return JSON.stringify(this.fieldValue) !== JSON.stringify(this.initialValue)
      }

      return this.fieldValue !== this.initialValue
    },
  },
  mounted() {
    // Store the initial prefilled value to detect edits
    if (this.originalValue !== undefined && this.originalValue !== null) {
      this.initialValue = this.deepClone(this.originalValue)
    } else if (this.fieldValue !== undefined && this.fieldValue !== null) {
      this.initialValue = this.deepClone(this.fieldValue)
    }
  },
  methods: {
    handleClear() {
      this.$emit("clear")
    },
    deepClone(value) {
      // Handle primitives and null
      if (value === null || typeof value !== "object") return value
      
      // Use structured cloning for deep copy of objects and arrays
      // This handles nested structures, dates, and other complex types
      try {
        return JSON.parse(JSON.stringify(value))
      } catch (e) {
        // Fallback to shallow clone if JSON methods fail (e.g., circular references)
        console.warn("Deep clone failed, using shallow clone:", e)
        if (Array.isArray(value)) return [...value]
        return { ...value }
      }
    },
  },
}
</script>
